import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Block as BlockIcon,
  Check as CheckIcon,
  ContentCopy as CopyIcon,
  DeleteForever as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { API_BASE_URL } from '../../config/constants';
import { getBackofficeAuthHeaders } from '../../services/api';

dayjs.extend(relativeTime);

export interface Contact {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  role: string;
  is_active: boolean;
  is_verified: boolean;
  is_primary_contact: boolean;
  last_login_at: string | null;
  created_at: string | null;
}

interface ContactsResponse {
  customer_id: string;
  contacts: Contact[];
  primary_contact_id: string | null;
}

interface Props {
  customerId: string;
  /** When this id changes, the tab refetches — useful when the parent
   *  reopens the same dialog after a customer edit. */
  refreshNonce?: number;
}

// Auth-headers + JSON wrapper. Falls back to a generic error string
// so the toast/Alert always has something useful to show.
async function api<T = any>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...getBackofficeAuthHeaders(),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body?.detail || detail;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail);
  }
  return res.json();
}

const CustomerContactsTab: React.FC<Props> = ({ customerId, refreshNonce }) => {
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null); // null = create mode
  const [form, setForm] = useState({ email: '', full_name: '', phone: '', make_primary: false });
  const [submitting, setSubmitting] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);

  // Magic-link dialog state — shown after Add Contact, after email
  // change, or when admin explicitly clicks "Resend activation" on a
  // pending row. Lets the admin copy the URL even if the email send
  // failed.
  const [linkDialog, setLinkDialog] = useState<{
    open: boolean;
    url: string;
    delivered: boolean;
    delivery: string;
    email: string;
    context: string;
  }>({ open: false, url: '', delivered: false, delivery: 'email', email: '', context: '' });

  const showLink = (
    url: string,
    delivered: boolean,
    delivery: string,
    email: string,
    context: string,
  ) => {
    if (!url) return;
    setLinkDialog({ open: true, url, delivered, delivery, email, context });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(linkDialog.url);
      setSuccessBanner('Activation link copied to clipboard.');
    } catch {
      // older browsers / non-https origin — fall back to manual select
      setDialogError('Copy failed — select the link manually.');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api<ContactsResponse>(`/backoffice/customers/${customerId}/contacts`);
      setContacts(data.contacts);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load, refreshNonce]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ email: '', full_name: '', phone: '', make_primary: false });
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEdit = (c: Contact) => {
    setEditingId(c.id);
    setForm({
      email: c.email,
      full_name: c.full_name ?? '',
      phone: c.phone ?? '',
      make_primary: c.is_primary_contact,
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const submitDialog = async () => {
    setSubmitting(true);
    setDialogError(null);
    try {
      if (editingId === null) {
        // Create
        const res = await api(`/backoffice/customers/${customerId}/contacts`, {
          method: 'POST',
          body: JSON.stringify({
            email: form.email.trim(),
            full_name: form.full_name.trim(),
            phone: form.phone.trim() || undefined,
            make_primary: form.make_primary,
            delivery_method: 'email',
          }),
        });
        setSuccessBanner(
          res?.delivered
            ? `Contact added. Activation email sent to ${form.email.trim()}.`
            : `Contact added. Email did NOT send — share the link below manually.`
        );
        // Show the link panel — admin can copy + paste into a chat /
        // sms / share screen, regardless of email delivery status.
        showLink(
          res?.magic_link_url,
          !!res?.delivered,
          'email',
          form.email.trim(),
          'Activation link for new contact',
        );
      } else {
        // Update — only send changed fields
        const original = contacts.find((c) => c.id === editingId);
        const patch: Record<string, string> = {};
        if (form.email.trim() && form.email.trim().toLowerCase() !== (original?.email ?? '').toLowerCase()) {
          patch.email = form.email.trim();
        }
        if (form.full_name !== (original?.full_name ?? '')) patch.full_name = form.full_name;
        if (form.phone !== (original?.phone ?? '')) patch.phone = form.phone;
        if (Object.keys(patch).length === 0) {
          setDialogError('No changes to save.');
          setSubmitting(false);
          return;
        }
        const res = await api(`/backoffice/customers/${customerId}/contacts/${editingId}`, {
          method: 'PATCH',
          body: JSON.stringify(patch),
        });
        if (res?.email_changed) {
          setSuccessBanner(
            res?.delivered
              ? `Email updated. Activation link sent to the new address.`
              : `Email updated. Email did NOT send — share the link below manually.`
          );
          showLink(
            res?.magic_link_url,
            !!res?.delivered,
            'email',
            form.email.trim(),
            'New activation link after email change',
          );
        } else {
          setSuccessBanner('Contact updated.');
        }
      }
      setDialogOpen(false);
      await load();
    } catch (e: any) {
      setDialogError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const promote = async (id: string) => {
    setActionInFlight(id);
    try {
      await api(`/backoffice/customers/${customerId}/contacts/${id}/promote`, { method: 'POST' });
      setSuccessBanner('Primary contact updated.');
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionInFlight(null);
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState<Contact | null>(null);

  const deleteContact = async () => {
    if (!deleteConfirm) return;
    const c = deleteConfirm;
    setActionInFlight(c.id);
    try {
      await api(`/backoffice/customers/${customerId}/contacts/${c.id}`, { method: 'DELETE' });
      setSuccessBanner(`Contact ${c.email} deleted. The email can now be added as a new contact.`);
      setDeleteConfirm(null);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionInFlight(null);
    }
  };

  const resendActivation = async (c: Contact) => {
    setActionInFlight(c.id);
    try {
      const res = await api(`/backoffice/customers/${customerId}/contacts/${c.id}/resend-activation`, {
        method: 'POST',
        body: JSON.stringify({ delivery_method: 'email' }),
      });
      setSuccessBanner(
        res?.delivered
          ? `Activation email resent to ${c.email}.`
          : `Email did NOT send — copy the link below and share manually.`,
      );
      showLink(
        res?.magic_link_url,
        !!res?.delivered,
        'email',
        c.email,
        'Fresh activation link',
      );
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionInFlight(null);
    }
  };

  const toggleActive = async (c: Contact) => {
    setActionInFlight(c.id);
    try {
      const action = c.is_active ? 'deactivate' : 'reactivate';
      await api(`/backoffice/customers/${customerId}/contacts/${c.id}/${action}`, { method: 'POST' });
      setSuccessBanner(c.is_active ? 'Contact deactivated.' : 'Contact reactivated.');
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionInFlight(null);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="subtitle1">
          Contacts ({contacts.length})
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh — useful after a contact completes activation in another tab">
            <span>
              <IconButton size="small" onClick={load} disabled={loading || actionInFlight !== null}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={openCreate}
            disabled={loading}
          >
            Add Contact
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successBanner && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessBanner(null)}>
          {successBanner}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Primary</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last login</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No contacts yet — click "Add Contact" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      {c.is_primary_contact ? (
                        <Tooltip title="Primary contact">
                          <StarIcon color="warning" />
                        </Tooltip>
                      ) : (
                        <Tooltip title={c.is_active ? 'Promote to primary' : 'Reactivate first, then promote'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => promote(c.id)}
                              disabled={!c.is_active || actionInFlight === c.id}
                            >
                              <StarBorderIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {c.full_name || '(no name)'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {c.email}
                      </Typography>
                      {c.phone && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {c.phone}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                        <Chip
                          label={c.is_active ? 'Active' : 'Inactive'}
                          color={c.is_active ? 'success' : 'default'}
                          size="small"
                        />
                        <Chip
                          icon={c.is_verified ? <CheckIcon /> : undefined}
                          label={c.is_verified ? 'Verified' : 'Pending activation'}
                          color={c.is_verified ? 'primary' : 'warning'}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {c.last_login_at ? (
                        <Tooltip title={dayjs(c.last_login_at).format('YYYY-MM-DD HH:mm')}>
                          <span>{dayjs(c.last_login_at).fromNow()}</span>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">Never</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit contact">
                        <span>
                          <IconButton size="small" onClick={() => openEdit(c)} disabled={actionInFlight === c.id}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {/* Resend activation: only for unverified active
                          rows. Already-verified contacts use password
                          reset instead — rotating their magic link
                          would let anyone with the URL take over.  */}
                      {c.is_active && !c.is_verified && (
                        <Tooltip title="Resend activation link">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => resendActivation(c)}
                              disabled={actionInFlight === c.id}
                              color="primary"
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip
                        title={
                          c.is_primary_contact
                            ? 'Promote another contact first'
                            : c.is_active
                              ? 'Deactivate (keep history)'
                              : 'Reactivate'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => toggleActive(c)}
                            disabled={c.is_primary_contact || actionInFlight === c.id}
                            color={c.is_active ? 'error' : 'success'}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {/* Hard delete — permanently removes the contact
                          and frees the email for reuse. Refused on the
                          primary; offered for everyone else so admins
                          can clean up typo'd emails without losing
                          the seat.  */}
                      <Tooltip
                        title={
                          c.is_primary_contact
                            ? 'Promote another contact first'
                            : 'Delete (frees the email for reuse)'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => setDeleteConfirm(c)}
                            disabled={c.is_primary_contact || actionInFlight === c.id}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId === null ? 'Add Contact' : 'Edit Contact'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {dialogError && <Alert severity="error">{dialogError}</Alert>}
            <TextField
              label="Full name"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              fullWidth
              autoFocus
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              fullWidth
              helperText={
                editingId !== null
                  ? 'Changing the email will require this contact to re-verify via a fresh activation link.'
                  : 'A magic-link activation email will be sent here.'
              }
            />
            <TextField
              label="Phone (optional)"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              fullWidth
            />
            {editingId === null && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  checked={form.make_primary}
                  onChange={(e) => setForm({ ...form, make_primary: e.target.checked })}
                />
                <Typography variant="body2">Promote to primary immediately</Typography>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submitDialog} disabled={submitting} variant="contained">
            {submitting ? <CircularProgress size={20} /> : (editingId === null ? 'Add' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete contact?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>{deleteConfirm?.full_name || deleteConfirm?.email}</strong>
              {deleteConfirm?.full_name && (
                <Typography component="span" variant="body2" color="text.secondary"> ({deleteConfirm?.email})</Typography>
              )}
            </Typography>
            <Alert severity="info">
              The email <strong>{deleteConfirm?.email}</strong> will be free for reuse —
              you can add it as a new contact again later (under this customer or any other).
              Orders and customer history aren't affected.
            </Alert>
            {!deleteConfirm?.is_active && (
              <Typography variant="caption" color="text.secondary">
                Tip: deactivated contacts keep their email reserved. Deleting frees it.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={actionInFlight !== null}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={deleteContact}
            disabled={actionInFlight !== null}
          >
            {actionInFlight === deleteConfirm?.id ? <CircularProgress size={20} /> : 'Delete contact'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={linkDialog.open}
        onClose={() => setLinkDialog({ ...linkDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{linkDialog.context}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity={linkDialog.delivered ? 'success' : 'warning'}>
              {linkDialog.delivered ? (
                <>Activation email sent to <strong>{linkDialog.email}</strong>.</>
              ) : (
                <>Email did NOT send. Share the link below with the contact manually.</>
              )}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              This link is single-use and expires in 72 hours.
            </Typography>
            <TextField
              value={linkDialog.url}
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={copyLink} startIcon={<CopyIcon />} variant="contained">
            Copy link
          </Button>
          <Button onClick={() => setLinkDialog({ ...linkDialog, open: false })}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerContactsTab;
